import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {DeviceService} from '../../../../../core/commons/device.service';
import {v4 as UUID} from 'uuid';
import {DurationPipe} from '../../../../../core/directives/duration.pipe';
import {DistancePipe} from '../../../../../core/directives/distance.pipe';
import {PricePipe} from '../../../../../core/directives/price.pipe';
import {StationPrice} from '../../../../../core/model/station-price';
import {GENERAL_STATION_GROUP_IDS, MonitorMarkerService} from '../../../../../core/map/monitor-marker.service';
import {escapeForOnclickArg} from '../../../../../core/map/map-popup-string.utils';
import {AuthStorage} from '../../../../../core/auth/auth-storage';
import {MapPOIEntry} from '../../tasks-manager/tasks-poi/model/map-poi.entry';
import {IntersectingMarkedRoadEntry} from '../form/model/intersecting-marked-road.entry';
import {LatestVehicleEntry} from '../../../../../core/component/latest-vehicles-info/model/latest-vehicle-entry';
import {PlaceSource} from '../../../../../core/component/places/model/place-source';
import {PlacesService} from '../../../../../core/component/places/places.service';
import {DueService} from '../../../../../core/component/latest-vehicles-info/model/due-service';
import {Permissions} from '../../../../../core/auth/permissions';
import {Module, Role} from '../../../../../core/model/profile';
import {PopupConfigsForm} from '../../../settings/global-config/popup-configs/popup-configs.form';
import {Observable} from 'rxjs';
import {ProTaskManagerUtilsComponent} from '../utils/pro-task-manager-utils.component';
import {PoiUnveroEntry} from '../form/model/unvero/poi-unvero.entry';
import {UnveroUtils} from '../../utils/unvero-utils';
import {UnveroStationProductEntry} from '../form/model/unvero/unvero-station-product.entry';
import {TaskUtils} from '../../tasks-manager/utils/task-utils';
import {VehiclePathPoint} from '../../../monitor/tacho/model/vehicle-path-point';
import {VehiclePathInfo} from '../../../monitor/tacho/model/vehicle-path-info';
import {ADBLUE_COLOR, DIESEL_COLOR} from '../../../../../core/component/fueling-poi/fueling-poi-marker.service';
import {AlarmType} from "../../../../../core/model/alarm-type";

export interface AddressData {
  uuid: string;
  location: google.maps.LatLng;
  selection: string;
  address: string;
}

export interface InfoWindowContent {
  content: string;
  bubbleHeight: number;
  bubbleWidth: number;
}

export const NUMBER_OF_NOTES_TO_SHOW = 2;

const isWebview = require("is-ua-webview");

@Injectable()
export class ProTasksMapMarkerService {

  private static readonly ADDRESS_TAG_START = '<div class="marker-address marker-data">';

  public static readonly QUEUE_NG_ZONE = 'onclick="window.queueMapComponentRef.zone.run(() => '
    + '{window.queueMapComponentRef.component.';

  public static readonly FORM_NG_ZONE = 'onclick="window.formMapComponentRef.zone.run(() => '
    + '{window.formMapComponentRef.component.';

  public static readonly DONE_NG_ZONE = 'onclick="window.doneMapComponentRef.zone.run(() => '
    + '{window.doneMapComponentRef.component.';

  constructor(private translate: TranslateService,
              private deviceService: DeviceService,
              private durationPipe: DurationPipe,
              private distancePipe: DistancePipe,
              private pricePipe: PricePipe,
              private placesService: PlacesService) {
  }

  public getMarkerInfoWindowContent(data: any): InfoWindowContent {
    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
      bubbleWidth: 245
    };
    let destination = data.destination;
    let address = destination.address;
    let doneAt = data.doneAt;
    let routeInfo = data.routeInfo;
    let comment = data.comment;
    info.content += '<div class="popup-content"><div class="ol-popup-header">'
      + `<span class="map-popup__title">${this.translate.instant('tasks.type.' + data.type)}</span></div>`;
    info.content += '<div class="ol-info-data">';
    ProTasksMapMarkerService.addInfoLine(info, '', `${destination.lat}, ${destination.lng}`, '');
    if (address) {
      ProTasksMapMarkerService.addInfoLine(info, '', address, '');
    }
    if (doneAt) {
      ProTasksMapMarkerService.addInfoLine(
        info,
        this.translate.instant('tasks.info.doneAt'),
        ': ' + doneAt,
        '');
    }
    if (routeInfo) {
      ProTasksMapMarkerService.addInfoLine(
        info,
        this.translate.instant('proTasks.form.routeInfo'),
        ': ' + routeInfo,
        '');
    }
    if (comment) {
      ProTasksMapMarkerService.addInfoLine(
        info,
        this.translate.instant('proTasks.form.comments'),
        ': ' + comment,
        '');
    }
    info.content += '</div>';
    return info;
  }

  public getTrafficInfoWindowContent(data: any): InfoWindowContent {
    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
      bubbleWidth: 245
    };
    this.generateTrafficContent(info, data);
    return info;
  }

  public getMarkedRoadInfoWindowContent(data: IntersectingMarkedRoadEntry): InfoWindowContent {
    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
      bubbleWidth: 245
    };
    this.generateMarkedRoadContent(info, data);
    return info;
  }

  public getStationsInfoWindowContent(poi: any, data: StationPrice[], ngZone?: string): InfoWindowContent {
    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
      bubbleWidth: 245
    };
    this.generateStationsContent(info, poi, data, ngZone);
    return info;
  }

  public getUnveroInfoWindowContent(station: PoiUnveroEntry, comment: string): InfoWindowContent {
    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
      bubbleWidth: 245
    };
    this.generateUnveroContent(info, station, comment);
    return info;
  }

  public getDoneTaskInfoWindowContent(data: any) {
    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
      bubbleWidth: 245
    };
    this.generateDoneTaskContent(info, data);
    return info;
  }

  public getPopupContent(
    latLng: any,
    address: string,
    uuid: any,
    zoom: any,
    poi?: MapPOIEntry,
    stationPrices?: StationPrice[],//todo remove
    formZone?: string) {

    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
      bubbleWidth: 245
    };
    let activeNgZone = ProTasksMapMarkerService.resolveActiveZone(formZone);
    let header = poi != null
      ? `<div class="ol-popup-header"><span class="map-popup__title">${poi.name}</span></div>`
      : '';
    info.content += '<div class="popup-content">' + header;
    info.content += '<div class="ol-info-data">';
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.coordinates')
      + `</span><a target="_blank" href="http://www.google.com/maps/place/${latLng.lat},${latLng.lng}">`
      + `: ${latLng.lat}, ${latLng.lng}</a>`
      + `<a class="map-popup__link" target="_blank" href="https://www.google.com/maps?q=&layer=c&cbll=${latLng.lat},${latLng.lng}">`
      + '<i class="ico-human"></i></a>'
      + '<a class="map-popup__link"'
      + 'title ="' + this.translate.instant('monitor.map.title.' + 'copy') + '"'
      + activeNgZone + `copyToClipboard('${latLng.lat},${latLng.lng}')` + '})">'
      + '<i class="ico-copy"></i></a></div></div>';
    info.bubbleHeight += 13;
    info.content += '<div class="ol-info-data">';
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.address')
      + `</span>: ${address}</div>`;
    if (address && address.length > 30) {
      info.bubbleHeight += 13;
    }
    if (poi != null) {
      ProTasksMapMarkerService.addStationInfo(info, poi, this.translate)
    }
    info.content += '<div class="popup-buttons">';
    if (poi != null) {
      if (!GENERAL_STATION_GROUP_IDS.includes(poi.poiGroup.id)) {
        info.content += ProTasksMapMarkerService.getIconWithLink(
          'editPOI(' + poi.id + ')',
          'ico-edit',
          'editPoi',
          this.translate,
          activeNgZone);
      }
      if (MonitorMarkerService.is5StepsHotel(poi.poiGroup.name)) {
        info.content += ProTasksMapMarkerService.getIconWithLink(
          'openHotelForm(' + poi.id + ')',
          'ico-hotel',
          'fiveStepsHotel',
          this.translate,
          activeNgZone);
      }
    } else {
      info.content += ProTasksMapMarkerService.getIconWithLink(
        `hideMarker('${uuid}')`,
        'ico-close',
        'close',
        this.translate,
        activeNgZone);
      if (this.canManageTasks()) {
        info.content += ProTasksMapMarkerService.getIconWithLink(
          'newPOI(\'' + latLng.lat + ', ' + latLng.lng + '\')',
          'ico-location',
          'poi',
          this.translate,
          activeNgZone);
      }
    }
    if (this.canManageTasks() && (formZone === 'queue' || formZone === 'form')) {
      info.content += ProTasksMapMarkerService.getIconWithLink(
        `addToRoute('${uuid}')`,
        'ico-location-arrow',
        'route',
        this.translate,
        activeNgZone);
    }

    if (this.canManageTasks()
      && !AuthStorage.isForbiddenManageTasks()
      && (formZone === 'form' || formZone === 'queue')) {

      let poiInfo = poi != null ? poi : {latitude: latLng.lat, longitude: latLng.lng, externalId: null, name: null};
      let type = poi != null ? poi.poiGroup.type : null;
      // eslint-disable-next-line max-len
      ProTasksMapMarkerService.addTaskInsertionButtons(info, poiInfo, this.translate, ProTaskManagerUtilsComponent.getPoiType(type), formZone)
    }
    info.content += '</div></div>';
    return info;
  }

  /*------------------------------------------------Vehicle Info window-----------------------------------------------*/

  public getVehicleInfoWindowContent(vehicle: LatestVehicleEntry, ngZone: string): Observable<InfoWindowContent> {
    let sources = [PlaceSource.NOMINATIM_COORDINATES];
    let coordinates = `${vehicle.position.latitude.toFixed(6)},${vehicle.position.longitude.toFixed(6)}`;
    let showButtons = ngZone !== 'done';
    let showVehicleLabels = this.showLabels();
    let activeNgZone = ProTasksMapMarkerService.resolveActiveZone(ngZone);
    return new Observable(infoWindow => {
      let info: InfoWindowContent = {
        content: '',
        bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
        bubbleWidth: 245
      };
      this.placesService.getPlaces(coordinates, sources).subscribe(
        data => {
          if (data.length > 1) {
            vehicle.address = data[1].name;
          }
          this.generateVehicleContent(vehicle, info, activeNgZone, showButtons, showVehicleLabels);
          infoWindow.next(info);
        },
        err => {
          vehicle.address = '';
          this.generateVehicleContent(vehicle, info, activeNgZone, showButtons, showVehicleLabels);
          infoWindow.next(info);
        }
      );
    });
  }

  private generateVehicleContent(vehicle: LatestVehicleEntry,
                                 info: InfoWindowContent,
                                 activeNgZone: string,
                                 showButtons: boolean,
                                 showVehicleLabels: boolean): void {

    info.content += this.showPlateNumberLabel(vehicle, activeNgZone)
      + this.showPlateNumberInput(vehicle, activeNgZone)
      + '<span>' + this.getCopyElement(vehicle.plateNumber, activeNgZone, 'copy-content') + '</span>';
    this.addDriver(vehicle, info, activeNgZone);
    info.content += '</div>';
    this.addMainContent(vehicle, info, activeNgZone, showVehicleLabels);
    this.addVehicleWarnings(vehicle, info);
    if (showButtons) {
      this.addButtons(vehicle, activeNgZone, info, showVehicleLabels);
    }
  }

  private showLabels(): boolean {
    return !Permissions.hasRole(Role.ROLE_PARTNER_EMPLOYEE)
      && (Permissions.hasModule(Module.PLANNING) || Permissions.hasModule(Module.TRAILER_SWAP));
  }

  private addHashtags(vehicle: LatestVehicleEntry, info: InfoWindowContent): void {
    if (vehicle.labels?.length > 0) {
      let lang = this.translate.currentLang;
      info.content += '<div class="popup-content"><span class="hashtags-content">';
      vehicle.labels.forEach(label => info.content += this.getHashtag(label.hashTag, label[lang]));
      info.content += '</span><div class="ol-popup-header"></div>';
    }
  }

  private getHashtag(hashtag: string, label: string): string {
    return '<div class="hashtag" title="'
      + label
      + '">#'
      + hashtag
      + '</div>';
  }

  private showPlateNumberLabel(vehicle: LatestVehicleEntry, activeNgZone: string) {
    return '<div class="popup-content"><div class="ol-popup-header">'
      + '<div id="plate-number-div" class="vehicle-plate-number">' + vehicle.plateNumber + '</div>'
      + '<span id="plate-number-span">' + '<a class="map-popup__link" '
      + activeNgZone
      + `editVehiclePlateNumber()})">`
      + '<i class="ico-edit"></i>' + '</a>'
      + '</span>';
  }

  private showPlateNumberInput(vehicle: LatestVehicleEntry, activeNgZone: string) {
    let plateNumber = escapeForOnclickArg(vehicle.plateNumber);
    return '<div id="plate-number-edit-div" class="vehicle-plate-number hide-plate-number">'
      + '<input id="plate-number-edit-input" class="hide-plate-number" type="text" '
      + `value="${plateNumber}" `
      + 'style="border: none;">'
      + '</div>'
      + '<span id="plate-number-edit-span" class="hide-plate-number">'
      + '<a class="map-popup__link" '
      + activeNgZone
      + `saveVehiclePlateNumber(${vehicle.id}, '${plateNumber}')`
      + '})">'
      + '<i class="ico-save"></i>' + '</a>'
      + '</span>';
  }

  private getCopyElement(text: string, activeNgZone: string, title: string) {
    text = escapeForOnclickArg(text);
    return '<a class="map-popup__link" '
      + 'title ="' + this.translate.instant('monitor.map.title.' + title) + '" '
      + activeNgZone
      + `copyTextToClipboard('${text}')` + '})">'
      + '<i class="ico-copy"></i></a>';
  }

  private addButtons(vehicle: LatestVehicleEntry,
                     ngZone: string,
                     info: InfoWindowContent,
                     showVehicleLabels: boolean): void {

    let plateNumber = escapeForOnclickArg(vehicle.plateNumber);
    info.content += '<div class="popup-buttons">';
    /*------------------------------------------Chat---------------------------------------*/
    if (vehicle.hasTab && AuthStorage.isLoggedClientEmployee()) {
      if (ngZone !== ProTasksMapMarkerService.FORM_NG_ZONE) {
        info.content += ProTasksMapMarkerService.getIconWithLink('previewTasks(' + vehicle.id + ')',
          'ico-check',
          'tasks',
          this.translate,
          ngZone);
      }
      if (!isWebview(navigator.userAgent)) {
        let driver = escapeForOnclickArg(vehicle.driver ? vehicle.driver.value : null);
        info.content += ProTasksMapMarkerService.getIconWithLink('openChat(' + vehicle.id + ', \''
          + plateNumber + '\', \'' + driver + '\', \''
          + vehicle.warnings.tabOffline + '\', \''
          + vehicle.driverLanguage + '\')',
          'ico-comment',
          'chat',
          this.translate,
          ngZone);
      }
      info.bubbleWidth += 5
    }
    /*------------------------------------------Tacho graph / table---------------------------------------*/
    if (vehicle.driver && vehicle.hasTacho && Permissions.hasModule(Module.HEAVY_TRACK)) {
      let secondDriverText = escapeForOnclickArg(vehicle.secondDriver ? vehicle.secondDriver.text : null);
      let secondDriverValue = escapeForOnclickArg(vehicle.secondDriver ? vehicle.secondDriver.value : null);
      const driverText = escapeForOnclickArg(vehicle.driver.text);
      const driverValue = escapeForOnclickArg(vehicle.driver.value);
      info.content += ProTasksMapMarkerService.getIconWithLink('showHideTacho(\'' + plateNumber + '\', '
        + vehicle.id + ', '
        + '\'CHART\', \'' + driverText + '\', \'' + driverValue + '\', \''
        + secondDriverText + '\', \'' + secondDriverValue + '\')',
        'ico-chart-bar',
        'charts',
        this.translate,
        ngZone);
      info.content += ProTasksMapMarkerService.getIconWithLink('showHideTacho(\'' + plateNumber + '\', '
        + vehicle.id + ', '
        + '\'TABLE\', \'' + driverText + '\', \'' + driverValue + '\', \''
        + secondDriverText + '\', \'' + secondDriverValue + '\')',
        'ico-table',
        'tables',
        this.translate,
        ngZone);
      info.bubbleWidth += 5;
    }
    /*------------------------------------------Track---------------------------------------*/
    if (vehicle.hasGPSDevice && (AuthStorage.isLoggedClientEmployee()
      || Permissions.hasModule(Module.HEAVY_TRACK))) {
      info.content += ProTasksMapMarkerService.getIconWithLink(`previewTrack(${vehicle.id})`,
        'ico-road',
        'drivingHistory',
        this.translate,
        ngZone);
      if (ngZone !== ProTasksMapMarkerService.FORM_NG_ZONE) {
        info.content += ProTasksMapMarkerService.getIconWithLink(`activateRoutePlanner(
        ${vehicle.position.latitude},
        ${vehicle.position.longitude},
        '${plateNumber}',
        ${vehicle.id},
        ${vehicle.fuelUsage})`,
          'ico-location-arrow', 'route',
          this.translate,
          ngZone);
      }
      info.bubbleWidth += 5;
    }
    /*------------------------------------------GPS CHART---------------------------------------*/
    info.content += ProTasksMapMarkerService.getIconWithLink('showGpsMoveChart(' + vehicle.id + ', ' +
      '\'' + plateNumber + '\')',
      'ico-chart-gantt',
      'gps-move',
      this.translate,
      ngZone);

    if (vehicle.hasGPSDevice) {
      let tapa = vehicle.tapa && Permissions.hasModule(Module.HEAVY_TRACK);
      if (tapa || vehicle.engineBlockControl || vehicle.doorsControl) {
        info.content += MonitorMarkerService.getIconWithLink(`openTapa(${vehicle.id}, ${tapa})`,
          'ico-shield',
          tapa ? 'open-tapa' : 'open-control',
          this.translate);
        info.bubbleWidth += 5;
      }
    }
    /*----------------------------------------VEHICLE LABELS-------------------------------------*/
    if (showVehicleLabels) {
      info.content += ProTasksMapMarkerService.getIconWithLink(`manageVehicleLabels(${vehicle.id},'${plateNumber}')`,
        'ico-hashtag',
        'manage-labels',
        this.translate,
        ngZone);
    }
  }

  private addMainContent(vehicle: LatestVehicleEntry,
                         info: InfoWindowContent,
                         activeNgZone: string,
                         showVehicleLabels: boolean): void {

    if (showVehicleLabels) {
      this.addHashtags(vehicle, info);
    }
    let popupConfigs = AuthStorage.getPopupConfigs();
    info.content += '<div class="ol-info-data">';
    if (popupConfigs.plateNumber) {
      info.content += '<div class="info-line">'
        + '<span class="info-label">'
        + this.translate.instant('vehicles.list.licensePlate')
        + '</span>'
        + '<span class="info-line" id="info-plate-number">' + ': ' + vehicle.plateNumber + ' ' + '' + '</span>'
        + '</div>';
      info.bubbleHeight += 13;
    }
    if (vehicle.address && popupConfigs.address) {
      info.content += '<div class="info-line">'
        + '<span class="info-label">' + this.translate.instant('monitor.vehicles.address') + '</span>: '
        + vehicle.address + ' '
        + this.getCopyElement(vehicle.address, activeNgZone, 'copy-content')
        + '</div>';
      info.bubbleHeight += 13;
    }
    if (vehicle.position.latitude && vehicle.position.longitude && popupConfigs.position) {
      info.content += '<div class="info-line"><span class="info-label">'
        + this.translate.instant('vehicles.dvr.track.infoWindow.location')
        + '</span>: <a target="_blank" href="http://www.google.com/maps/place/'
        + vehicle.position.latitude + ',' + vehicle.position.longitude + '" class="map-popup__link">'
        + vehicle.position.latitude + ', ' + vehicle.position.longitude + '</a>'
        + '<a class="map-popup__link" target="_blank" href="https://www.google.com/maps?q=&layer=c&cbll='
        + vehicle.position.latitude + ',' + vehicle.position.longitude + '">'
        + '<i class="ico-human"></i></a>'
        + this.getCopyElement(vehicle.position.latitude
          + ','
          + vehicle.position.longitude, activeNgZone, 'copy-content')
        + '</div>';
      info.bubbleHeight += 13;
    }

    if (vehicle.speed && popupConfigs.speed) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.speed'),
        vehicle.speed,
        this.translate.instant('unit.kmph'));
    }
    if (vehicle.fuel && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.fuel) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.fuel'),
        vehicle.fuel,
        this.translate.instant('unit.l'));
    }
    if (vehicle.adblueLevelPercent && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.adblueLevelPercent) {
      let adBlue = vehicle.adBlueTankCapacity
        ? vehicle.adBlueTankCapacity / 100 * vehicle.adblueLevelPercent
        : vehicle.adblueLevelPercent;
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.adblueLevel'),
        adBlue.toFixed(2),
        this.translate.instant(vehicle.adBlueTankCapacity ? 'unit.l' : 'unit.percent'));
    }
    if (vehicle.float1Liters && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.fuel) {
      let liters: string | number;
      if (vehicle.float2Liters) {
        let sum: number = vehicle.float1Liters + vehicle.float2Liters;
        liters = '(' + vehicle.float1Liters + '+' + vehicle.float2Liters + ')=' + sum;
      } else {
        liters = vehicle.float1Liters;
      }
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.floatLiters'),
        liters,
        this.translate.instant('unit.l'));
    }

    if (vehicle.axleWeight && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.axleWeight) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.axleWeight'),
        vehicle.axleWeight,
        this.translate.instant('unit.kg'));
    }
    if (vehicle.axleWeight2 && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.axleWeight2) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.axleWeight2'),
        vehicle.axleWeight2,
        this.translate.instant('unit.kg'));
    }
    if (vehicle.grossCombinationVehicleWeight != null
      && Permissions.hasModule(Module.HEAVY_TRACK)
      && popupConfigs.grossCombinationVehicleWeight) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.grossCombinationVehicleWeight'),
        vehicle.grossCombinationVehicleWeight,
        this.translate.instant('unit.kg'));
    }
    if (vehicle.tabTime && popupConfigs.tabTime) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.tab-gps-update'),
        vehicle.tabTime,
        '');
    }
    if (vehicle.time && popupConfigs.time) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.gps-update'),
        vehicle.time,
        '');
    }
    if (vehicle.distance && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.distance) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.distance'),
        vehicle.distance,
        this.translate.instant('unit.km'));
    }
    if (vehicle.gpsOdometer && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.distance) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.gpsOdometer'),
        vehicle.gpsOdometer,
        this.translate.instant('unit.km'));
    }
    if (vehicle.distanceToday && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.distanceToday) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.distanceToday'),
        vehicle.distanceToday,
        this.translate.instant('unit.km'));
    }
    if (vehicle.taskDistance
      && Permissions.hasModule(Module.HEAVY_TRACK)
      && vehicle.taskCount > 0
      && popupConfigs.taskDistance) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.taskDistance'),
        (vehicle.taskDistance / 1000).toFixed(1),
        this.translate.instant('unit.km'));
    }
    if (vehicle.externalPowerV && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.externalPowerV) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.externalPowerV'),
        vehicle.externalPowerV / 1000,
        this.translate.instant('unit.V'));
    }
    if (vehicle.engineHours && Permissions.hasModule(Module.HEAVY_TRACK) && popupConfigs.engineHours) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.engineHours'),
        vehicle.engineHours,
        this.translate.instant('unit.h'));
    }
    if (vehicle.connectedTrailer != null && Permissions.hasModule(Module.HEAVY_TRACK)) {
      this.addConnectedTrailerData(vehicle, popupConfigs, info);
    }
    this.addVehicleTemperatureData(vehicle, popupConfigs, info);
    if (vehicle.driverSimNo && popupConfigs.driverPhoneNumber) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.driverSimNo'),
        vehicle.driverSimNo,
        '');
    }
    if (vehicle.vehiclePhoneNumber && popupConfigs.vehiclePhoneNumber) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.vehiclePhoneNumber'),
        vehicle.vehiclePhoneNumber,
        '');
    }
    if (vehicle.securityMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.LOOP_OFF'),
        vehicle.warnings.alarms.includes(AlarmType.LOOP_OFF) ? 'OFF' : 'ON',
        '');
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.DOORS_OFF'),
        vehicle.warnings.alarms.includes(AlarmType.DOORS_OFF) ? 'OFF' : 'ON',
        '');
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.LOOP_ALARM'),
        vehicle.warnings.alarms.includes(AlarmType.LOOP_ALARM) ? 'ON' : 'OFF',
        '');
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.DOORS_ALARM'),
        vehicle.warnings.alarms.includes(AlarmType.DOORS_ALARM) ? 'ON' : 'OFF',
        '');
    }
    info.content += '</div>';
  }

  private addVehicleTemperatureData(vehicle: LatestVehicleEntry, popupSettings: PopupConfigsForm, info: InfoWindowContent) {
    // ---------------------------- First compartment -----------------------------------
    if (vehicle.setFreezerTemperature != null && popupSettings.setFreezerTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.setFreezerTemperature'),
        vehicle.setFreezerTemperature,
        this.translate.instant('unit.t'));
    }
    const connectedTrailer = vehicle.connectedTrailer;
    if (vehicle.supplyFreezerTemperature != null && connectedTrailer == null && popupSettings.supplyFreezerTemperature
      || (vehicle.supplyFreezerTemperature != null &&
        connectedTrailer != null
        && connectedTrailer.supplyFreezerTemperature == null)) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.supplyFreezerTemperature'),
        vehicle.supplyFreezerTemperature,
        this.translate.instant('unit.t'));
    }
    if (vehicle.actualFreezerTemperature != null && popupSettings.actualFreezerTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.actualFreezerTemperature'),
        vehicle.actualFreezerTemperature,
        this.translate.instant('unit.t'));
    }
    if (vehicle.freezerEngineOn != null && popupSettings.freezerEngineOn) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerEngineOn'),
        vehicle.freezerEngineOn
          ? this.translate.instant('monitor.vehicles.ON')
          : this.translate.instant('monitor.vehicles.OFF'),
        '');
    }
    if (vehicle.freezerMode != null && popupSettings.freezerMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerMode'),
        this.translate.instant('monitor.vehicles.freezerModes.' + vehicle.freezerMode),
        '');
    }
    // ---------------------------- Second compartment -----------------------------------
    if (vehicle.secondCompartmentSetPoint != null && popupSettings.secondCompartmentSetPoint) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentSetPoint'),
        vehicle.secondCompartmentSetPoint,
        this.translate.instant('unit.t'));
    }
    if (vehicle.secondCompartmentSupplyTemperature != null && popupSettings.secondCompartmentSupplyTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentSupplyTemperature'),
        vehicle.secondCompartmentSupplyTemperature,
        this.translate.instant('unit.t'));
    }
    if (vehicle.secondCompartmentTemperature != null && popupSettings.secondCompartmentTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentTemperature'),
        vehicle.secondCompartmentTemperature,
        this.translate.instant('unit.t'));
    }
    if (vehicle.secondCompartmentFreezerMode != null && popupSettings.secondCompartmentFreezerMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentFreezerMode'),
        vehicle.secondCompartmentFreezerMode,
        '');
    }
    if (vehicle.secondCompartmentIsOn != null && popupSettings.secondCompartmentIsOn) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentIsOn'),
        vehicle.secondCompartmentIsOn
          ? this.translate.instant('monitor.vehicles.ON')
          : this.translate.instant('monitor.vehicles.OFF'),
        '');
    }
    // ---------------------------- Sensor temps ----------------------------------------
    if (vehicle.firstSensorTemperature != null && popupSettings.firstSensorTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.firstSensorTemperature'),
        vehicle.firstSensorTemperature,
        this.translate.instant('unit.t'));
    }
    if (vehicle.secondSensorTemperature != null && popupSettings.secondSensorTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondSensorTemperature'),
        vehicle.secondSensorTemperature,
        this.translate.instant('unit.t'));
    }
    if (vehicle.thirdSensorTemperature != null && popupSettings.thirdSensorTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.thirdSensorTemperature'),
        vehicle.thirdSensorTemperature,
        this.translate.instant('unit.t'));
    }
    if (vehicle.fourthSensorTemperature != null && popupSettings.fourthSensorTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.fourthSensorTemperature'),
        vehicle.fourthSensorTemperature,
        this.translate.instant('unit.t'));
    }
    // ---------------------------- Other -----------------------------------------------
    if (vehicle.ambientTemperature != null && popupSettings.ambientTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.ambientTemperature'),
        vehicle.ambientTemperature,
        this.translate.instant('unit.t'));
    }
    if (vehicle.freezerPowerMode != null && popupSettings.freezerPowerMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerPowerMode'),
        this.translate.instant('monitor.vehicles.freezerPowerModes.' + vehicle.freezerPowerMode),
        '');
    }
    if (vehicle.freezerRunMode != null && popupSettings.freezerRunMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerRunMode'),
        this.translate.instant('monitor.vehicles.freezerRunModes.' + vehicle.freezerRunMode),
        '');
    }
    if (vehicle.freezerDieselHours != null && popupSettings.freezerDieselHours) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerDieselHours'),
        vehicle.freezerDieselHours,
        this.translate.instant('unit.h'));
    }
    if (vehicle.freezerElectricHours != null && popupSettings.freezerElectricHours) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerElectricHours'),
        vehicle.freezerElectricHours,
        this.translate.instant('unit.h'));
    }
    if (vehicle.freezerStandbyHours != null && popupSettings.freezerStandbyHours) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerStandbyHours'),
        vehicle.freezerStandbyHours,
        this.translate.instant('unit.h'));
    }
    if (vehicle.engineTemperature != null && popupSettings.engineTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.engineTemperature'),
        vehicle.engineTemperature,
        this.translate.instant('unit.t'));
    }
  }

  private addConnectedTrailerData(vehicle: LatestVehicleEntry, popupSettings: PopupConfigsForm, info: InfoWindowContent) {
    // ---------------------------- First compartment -----------------------------------
    const trailer = vehicle.connectedTrailer;
    if (trailer.setFreezerTemperature != null
      && vehicle.setFreezerTemperature == null && popupSettings.setFreezerTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.setFreezerTemperature'),
        trailer.setFreezerTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.supplyFreezerTemperature != null
      && popupSettings.supplyFreezerTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.supplyFreezerTemperature'),
        trailer.supplyFreezerTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.actualFreezerTemperature != null
      && vehicle.actualFreezerTemperature == null && popupSettings.actualFreezerTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.actualFreezerTemperature'),
        trailer.actualFreezerTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.freezerMode != null && popupSettings.freezerMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerMode'),
        this.translate.instant('monitor.vehicles.freezerModes.' + trailer.freezerMode),
        '');
    }
    if (trailer.freezerEngineOn != null
      && vehicle.freezerEngineOn == null && popupSettings.freezerEngineOn) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerEngineOn'),
        trailer.freezerEngineOn
          ? this.translate.instant('monitor.vehicles.ON')
          : this.translate.instant('monitor.vehicles.OFF'),
        '');
    }
    // ---------------------------- Second compartment -----------------------------------
    if (trailer.secondCompartmentSetPoint != null
      && vehicle.secondCompartmentSetPoint == null && popupSettings.secondCompartmentSetPoint) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentSetPoint'),
        trailer.secondCompartmentSetPoint,
        this.translate.instant('unit.t'));
    }
    if (trailer.secondCompartmentSupplyTemperature != null
      && vehicle.secondCompartmentSupplyTemperature == null && popupSettings.secondCompartmentSupplyTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentSupplyTemperature'),
        trailer.secondCompartmentSupplyTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.secondCompartmentTemperature != null
      && vehicle.secondCompartmentTemperature == null && popupSettings.secondCompartmentTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentTemperature'),
        trailer.secondCompartmentTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.secondCompartmentFreezerMode != null
      && vehicle.secondCompartmentFreezerMode == null && popupSettings.secondCompartmentFreezerMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentFreezerMode'),
        trailer.secondCompartmentFreezerMode,
        this.translate.instant('unit.t'));
    }
    if (trailer.secondCompartmentIsOn != null
      && vehicle.secondCompartmentIsOn == null && popupSettings.secondCompartmentIsOn) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondCompartmentIsOn'),
        trailer.secondCompartmentIsOn
          ? this.translate.instant('monitor.vehicles.ON')
          : this.translate.instant('monitor.vehicles.OFF'),
        '');
    }
    // ---------------------------- Sensor temps ----------------------------------------
    if (trailer.firstSensorTemperature
      && vehicle.firstSensorTemperature == null && popupSettings.firstSensorTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.firstSensorTemperature'),
        trailer.firstSensorTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.secondSensorTemperature
      && vehicle.secondSensorTemperature == null && popupSettings.secondSensorTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.secondSensorTemperature'),
        trailer.secondSensorTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.thirdSensorTemperature
      && vehicle.thirdSensorTemperature == null && popupSettings.thirdSensorTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.thirdSensorTemperature'),
        trailer.thirdSensorTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.fourthSensorTemperature
      && vehicle.fourthSensorTemperature == null && popupSettings.fourthSensorTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.fourthSensorTemperature'),
        trailer.fourthSensorTemperature,
        this.translate.instant('unit.t'));
    }
    // ---------------------------- Other -----------------------------------------------
    if (trailer.ambientTemperature != null && popupSettings.ambientTemperature) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.ambientTemperature'),
        trailer.ambientTemperature,
        this.translate.instant('unit.t'));
    }
    if (trailer.freezerPowerMode != null && popupSettings.freezerPowerMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerPowerMode'),
        this.translate.instant('monitor.vehicles.freezerPowerModes.' + trailer.freezerPowerMode),
        '');
    }
    if (trailer.freezerRunMode != null && popupSettings.freezerRunMode) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerRunMode'),
        this.translate.instant('monitor.vehicles.freezerRunModes.' + trailer.freezerRunMode),
        '');
    }
    if (trailer.freezerDieselHours != null && popupSettings.freezerDieselHours) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerDieselHours'),
        trailer.freezerDieselHours,
        this.translate.instant('unit.h'));
    }
    if (trailer.freezerElectricHours != null && popupSettings.freezerElectricHours) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerElectricHours'),
        trailer.freezerElectricHours,
        this.translate.instant('unit.h'));
    }
    if (trailer.freezerStandbyHours != null && popupSettings.freezerStandbyHours) {
      ProTasksMapMarkerService.createInfoLine(
        info,
        this.translate.instant('monitor.vehicles.freezerStandbyHours'),
        trailer.freezerStandbyHours,
        this.translate.instant('unit.h'));
    }
  }

  // ---------------------------- Driver path ----------------------------------------

  public createPathInfoWindow(infos: VehiclePathInfo[],
                              distance: number,
                              uuid: string,
                              zone: string): InfoWindowContent {

    let startPoint = infos[0].startPoint;
    let endPoint = infos.length > 1
      ? infos[infos.length - 1].endPoint
      : infos[0].endPoint;
    let range = startPoint.time + ' - ' + endPoint.time;

    let info: InfoWindowContent = {
      content: '',
      bubbleHeight: this.deviceService.isSafariBrowser() ? 74 : 64,
      bubbleWidth: 245
    };

    this.addPathInfoWindowActivity(range, distance, info);
    this.addPathInfoWindowVehicles(infos, info);
    this.addPathInfoWindowButtons(uuid, endPoint, zone, info);
    return info;
  }

  private addPathInfoWindowActivity(range: string, distance: number, info: InfoWindowContent): void {
    let timeRange = '<div class="info-line"><span class="info-label">'
      + this.translate.instant('tacho.map.range')
      + `</span>: ${range}</div>`;
    let distanceDiv = '<div class="info-line"><span class="info-label">'
      + this.translate.instant('tacho.map.distance')
      + `</span>: ${distance} ` + this.translate.instant('unit.km') + `</div>`;
    info.content += '<div class="popup-content">';
    info.content += `<div class="ol-info-data">${timeRange}</div>`;
    info.content += `<div class="ol-info-data">${distanceDiv}</div>`;
  }

  private addPathInfoWindowVehicles(infos: VehiclePathInfo[], info: InfoWindowContent): void {
    infos.forEach(vehicle => {
      info.content += '</span><div class="ol-popup-header"></div>';
      let vehiclePlateNumber = '<div class="info-line"><span class="info-label">'
        + this.translate.instant('tacho.map.vehicle')
        + `</span>: ${vehicle.plateNumber}</div>`;
      let startTime = '<div class="info-line"><span class="info-label">'
        + this.translate.instant('tacho.map.startTime')
        + `</span>: ${vehicle.startPoint.time}</div>`;
      let startAddress = '<div class="info-line"><span class="info-label">'
        + this.translate.instant('tacho.map.startAddress')
        + `</span>: ${vehicle.startPoint.address}</div>`;
      let endTime = '<div class="info-line"><span class="info-label">'
        + this.translate.instant('tacho.map.endTime')
        + `</span>: ${vehicle.endPoint.time}</div>`;
      let endAddress = '<div class="info-line"><span class="info-label">'
        + this.translate.instant('tacho.map.endAddress')
        + `</span>: ${vehicle.endPoint.address}</div>`;
      info.content += `<div class="ol-info-data">${vehiclePlateNumber}</div>`;
      info.content += `<div class="ol-info-data">${startTime}</div>`;
      info.content += `<div class="ol-info-data">${startAddress}</div>`;
      info.content += `<div class="ol-info-data">${endTime}</div>`;
      info.content += `<div class="ol-info-data">${endAddress}</div>`;
    });
  }

  private addPathInfoWindowButtons(uuid: string,
                                   point: VehiclePathPoint,
                                   zone: string,
                                   info: InfoWindowContent): void {

    let activeNgZone = ProTasksMapMarkerService.resolveActiveZone(zone);
    if (AuthStorage.isLoggedClientEmployee()) {
      info.content += ProTasksMapMarkerService.getIconWithLink(
        'newPOI(\'' + point.latitude + ', ' + point.longitude + '\')',
        'ico-location',
        'poi',
        this.translate,
        activeNgZone);
    }
    info.content += ProTasksMapMarkerService.getIconWithLink(
      `hidePathAndMarker('${uuid}')`,
      'ico-close',
      'close',
      this.translate,
      activeNgZone);
  }

  private addDriver(vehicle: LatestVehicleEntry, info: InfoWindowContent, activeNgZone: string): void {
    if (vehicle.driver) {
      info.content += '<div class="driver-text"> '
        + vehicle.driver.value
        + this.getCopyElement(vehicle.driver.value, activeNgZone, 'copy-content')
        + '</div>';
    }
  }

  private addVehicleWarnings(vehicle: LatestVehicleEntry, info: InfoWindowContent): void {
    const lateOrSoonNotes = vehicle.noteStatuses != null
      ? vehicle.noteStatuses.filter(v => v.noteStatus === 'LATE' || v.noteStatus === 'SOON')
      : [];
    if ((vehicle.warnings.gpsOffline
        || vehicle.warnings.alarms.length > 0
        || vehicle.hasDvr && vehicle.warnings.dvrOffline
        || vehicle.serviceInRed !== null
        || lateOrSoonNotes.length > 0)
      && AuthStorage.isLoggedClientEmployee()) {

      info.content += '<div class="map-popup__status">';
      info.bubbleHeight += 13;
      if (vehicle.warnings.gpsOffline) {
        info.bubbleHeight += 13;
        info.content += '<div class="status--warning">'
          + this.translate.instant('monitor.vehicles.gpsDeviceOff')
          + '</div>';
      }

      if (vehicle.warnings.alarms) {
        vehicle.warnings.alarms.forEach(alarm => {
          info.bubbleHeight += 13;
          info.content += '<div class="status--warning">'
            + this.translate.instant('monitor.vehicles.alarm.' + alarm) + '</div>';
        });
      }

      if (vehicle.hasDvr && vehicle.warnings.dvrOffline) {
        info.bubbleHeight += 29;
        info.content += '<div class="status--warning">'
          + this.translate.instant('monitor.vehicles.dvrOff')
          + '<span class="status__details">'
          + this.translate.instant('monitor.vehicles.dvrOffDate')
          + vehicle.lastDVRTime
          + '</span></div>';
      }
      if (vehicle.serviceInRed !== null && vehicle.dueServices !== null) {
        for (let service of vehicle.dueServices) {
          info.bubbleHeight += 13;
          if (service.serviceInRed) {
            info.content += '<div class="status--warning">'
              + this.translate.instant('monitor.vehicles.serviceInRed')
              + service.serviceName;
          } else {
            info.content += '<div class="status--alert">'
              + this.translate.instant('monitor.vehicles.serviceInYellow')
              + service.serviceName;
          }
          info.content += '</div>';
        }
      }
      const noteCount = lateOrSoonNotes.length;
      if (noteCount > 0) {
        lateOrSoonNotes.sort((note1, note2) => note1.noteStatus === 'LATE' ? -1 : 0)
          .splice(0, NUMBER_OF_NOTES_TO_SHOW)
          .forEach(note => {
            const noteMessage = note.dueNote.value && note.dueNote.value !== ''
              ? note.dueNote.value
              : this.translate.instant('monitor.vehicles.noteIsNull')
            info.bubbleHeight += 13;
            info.content += '<div Class="status--' + note.noteStatus + '">'
              + this.translate.instant('monitor.vehicles.noteIs' + note.noteStatus)
              + noteMessage;
            info.content += '</div>';
          });
        if (noteCount > NUMBER_OF_NOTES_TO_SHOW) {
          info.content += '<div class="status__more">'
            + this.translate.instant('monitor.vehicles.moreDueNotes');
          info.content += '</div>';
        }
      }
      info.content += '</div>';
    }
  }

  private getDurationTooltipValue(service: DueService): string {
    if (service.type === 'MONTHS') {
      return `${this.durationPipe.transform(service.leftTillService, ['MONTHS_DAYS'])}`;
    }
    return `${service.leftTillService} ${this.translate.instant('monitor.vehicles.service.type.' + service.type)}`;
  }

  private generateStationsContent(info: InfoWindowContent, poi: any, stationPrices: StationPrice[], ngZone?: string) {//todo remove
    let lat = +poi.latitude;
    let lng = +poi.longitude;
    info.content += '<div class="popup-content"><div class="ol-popup-header">'
      + `<span class="map-popup__title">${poi.name}</span></div>`;
    info.content += '<div class="ol-info-data">';
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.address')
      + `</span>: ${poi.address}</div>`;
    if (poi.address && poi.address.length > 30) {
      info.bubbleHeight += 13;
    }
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.coordinates')
      + `</span><a target="_blank" href="http://www.google.com/maps/place/${lat},${lng}">`
      + `: ${lat.toFixed(6)}, ${lng.toFixed(6)}</a>`
      + `<a class="map-popup__link" target="_blank"`
      + `href="https://www.google.com/maps?q=&layer=c&cbll=${lat},${lng}">`
      + '<i class="ico-human"></i></a>'
      + '<a class="map-popup__link"'
      + 'title ="' + this.translate.instant('monitor.map.title.' + 'copy') + '"'
      + ProTasksMapMarkerService.FORM_NG_ZONE + `copyToClipboard('${lat},${lng}')` + '})">'
      + '<i class="ico-copy"></i></a></div></div>';
    info.bubbleHeight += 13;
    ProTasksMapMarkerService.addStationInfo(info, poi, this.translate);
    if (poi.parkingDetails) {
      MonitorMarkerService.addPoiParkingDetails(poi.parkingDetails, info, this.translate);
    }
    info.content += '<div class="popup-buttons">';
    ProTasksMapMarkerService.addTaskInsertionButtons(
      info,
      poi,
      this.translate,
      ProTaskManagerUtilsComponent.getPoiType(poi.poiGroup.type),
      ngZone);
    info.content += '</div></div>';
  }

  private generateUnveroContent(info: InfoWindowContent, station: PoiUnveroEntry, comment: string): void {
    let lat = station.lat;
    let lng = station.lng;
    info.content += '<div class="popup-content"><div class="ol-popup-header">'
      + `<span class="map-popup__title">${station.partnerName}</span></div>`;
    info.content += '<div class="ol-info-data">';
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('tasks.map.unveroPopup.brand')
      + `</span>: ${station.brand}</div>`;
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('tasks.map.unveroPopup.stationId')
      + `</span>: ${station.gasStationId}</div>`;
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.coordinates')
      + `</span><a target="_blank" href="http://www.google.com/maps/place/${lat},${lng}">`
      + `: ${lat.toFixed(6)}, ${lng.toFixed(6)}</a>`
      + `<a class="map-popup__link" target="_blank"`
      + `href="https://www.google.com/maps?q=&layer=c&cbll=${lat},${lng}">`
      + '<i class="ico-human"></i></a>'
      + '<a class="map-popup__link"'
      + 'title ="' + this.translate.instant('monitor.map.title.' + 'copy') + '"'
      + ProTasksMapMarkerService.FORM_NG_ZONE + `copyToClipboard('${lat},${lng}')` + '})">'
      + '<i class="ico-copy"></i></a></div></div>';
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.address')
      + `</span>: ${station.address}</div>`;
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('tasks.map.unveroPopup.hasParking')
      + `</span>: <i class="${station.hasParking ? 'ico-check' : 'ico-close'}"
      + style="color: ${station.hasParking ? '#00a65a' : '#b63019'}"></i></div>`;
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('tasks.map.unveroPopup.hasShop')
      + `</span>: <i class="${station.hasShop ? 'ico-check' : 'ico-close'}"
      + style="color: ${station.hasShop ? '#00a65a' : '#b63019'}"></i></div>`;
    if (station.products && station.products.length > 0) {
      this.insertUnveroProducts(info, station.products);
    }
    if (station.gasStationComment != null && station.gasStationComment !== '') {
      ProTasksMapMarkerService.addInfoLine(
        info,
        this.translate.instant('monitor.poi.comment'),
        ': ' + station.gasStationComment,
        '');
    }
    info.content += '<div class="popup-buttons">';
    ProTasksMapMarkerService.addUnveroTaskInsertionButtons(info, station, this.translate, comment);
    info.content += '</div></div>';
  }

  private static addUnveroTaskInsertionButtons(info: InfoWindowContent,
                                               station: PoiUnveroEntry,
                                               translate: TranslateService,
                                               comment: string): void {

    comment = escapeForOnclickArg(comment)
    info.content +=
      this.unveroTaskInsertActionLink(station, 'autoImport', translate, false, comment, false);
    info.content +=
      this.unveroTaskInsertActionLink(station, 'importToLastPosition', translate, true, comment, false);
    info.content +=
      this.unveroTaskInsertActionLink(station, 'importSelected', translate, false, comment, true);
  }

  private insertUnveroProducts(info: InfoWindowContent, products: UnveroStationProductEntry[]) {
    products.sort((a, b) => a.name < b.name ? 1 : -1);
    for (let i = 0; i < products.length; i++) {
      let product: any = products[i];
      if (product.price && product.price > 0) {
        let color = product.name === 'DIESEL' ? DIESEL_COLOR : ADBLUE_COLOR;
        info.content += '<div class="fuel-prices">';
        info.content +=
          '<span class="fuel-type" style="background-color: ' + color + '">'
          + product.name + '</span>'
          + '<span class="info-label">' + product.price + ' ' + product.currency + '/L' + '</span>';
        if (product.totalCost && product.totalCost > 0) {
          info.content += '<span class="info-label">'
            + '. '
            + this.translate.instant('tasks.map.unveroPopup.totalCost')
            + ': '
            + product.totalCost + product.currency + '</span>';
        }
        info.content += '</div>';
      }
    }
  }

  private static unveroTaskInsertActionLink(station: PoiUnveroEntry,
                                            tooltip: string,
                                            translate: TranslateService,
                                            autoInsert: boolean,
                                            comment: string,
                                            importSelected: boolean): string {

    let contactlessType = UnveroUtils.resolveUnveroContactless(station);
    let externalId = contactlessType != null ? station.externalPoiId : null;
    return '<a class="icon-action"'
      + this.FORM_NG_ZONE
      + `insertProTask(
        ${station.lat},
        ${station.lng},
        ${autoInsert},
        '${externalId}',
        '${null}',
        '${comment}',
        'FUELLING',
        '${contactlessType}',
        '${null}',
        '${null}',
        '${null}',
        ${importSelected},
        '${station.gasStationId}',)})">`
      + `${translate.instant('proTasks.map.' + tooltip)}`
      + '</a>';
  }

  private generateDoneTaskContent(info: InfoWindowContent, data: any) {
    let lat = +data.latitude;
    let lng = +data.longitude;
    info.content += '<div class="ol-info-data">';
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.address')
      + `</span>: ${data.address}</div>`;
    if (data.address && data.address.length > 30) {
      info.bubbleHeight += 13;
    }
    info.content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('address-search.coordinates')
      + `</span><a target="_blank" href="http://www.google.com/maps/place/${lat},${lng}">`
      + `: ${lat.toFixed(6)}, ${lng.toFixed(6)}</a>`
      + `<a class="map-popup__link" target="_blank"`
      + `href="https://www.google.com/maps?q=&layer=c&cbll=${lat},${lng}">`
      + '<i class="ico-human"></i></a>'
      + '<a class="map-popup__link"'
      + 'title ="' + this.translate.instant('monitor.map.title.' + 'copy') + '"'
      + ProTasksMapMarkerService.DONE_NG_ZONE + `copyToClipboard('${lat},${lng}')` + '})">'
      + '<i class="ico-copy"></i></a></div></div>';
    info.bubbleHeight += 13;
    info.content += '</div></div>'
  }

  public getAddressData(lat: number, lng: number): AddressData {
    return {
      uuid: UUID(),
      location: new google.maps.LatLng(lat, lng),
      selection: null,
      address: ''
    };
  }

  private static getActionLink(
    action: string,
    icon: string,
    tooltip: string,
    translate: TranslateService): string {

    return '<a class="icon-action" '
      + 'title ="' + translate.instant('monitor.map.title.' + tooltip) + '"'
      + 'onclick="window.formMapComponentRef.zone.run(() => '
      + '{window.formMapComponentRef.component.' + action + '})"><i class="' + icon + '"></i></a>';
  }

  private static getQueueActionLink(action: string,
                                    icon: string,
                                    tooltip: string,
                                    translate: TranslateService): string {

    return '<a class="icon-action" '
      + 'title ="' + translate.instant('monitor.map.title.' + tooltip) + '"'
      + 'onclick="window.queueMapComponentRef.zone.run(() => '
      + '{window.queueMapComponentRef.component.' + action + '})"><i class="' + icon + '"></i></a>';
  }

  private static getStringWithLink(action: string, text: string, translate: TranslateService): string {
    return '<a class="icon-action" '
      + 'onclick="window.formMapComponentRef.zone.run(() => ' +
      '{window.formMapComponentRef.component.' + action + '})">' + translate.instant('proTasks.map.' + text) + '</a>';
  }

  private generateTrafficContent(info: InfoWindowContent, data: any) {
    info.content += '<div class="popup-content"><div class="ol-popup-header">'
      + `<span class="map-popup__title">${this.translate.instant('proTasks.map.' + data.type)}</span></div>`;
    info.content += '<div class="ol-info-data">';
    info.content += `<div style="text-align: center" class="info-line">${data.text}</div>`;
    if (data.from != null && data.from !== '') {
      info.content += '<div style="text-align: center" class="info-line">'
        + this.translate.instant('proTasks.map.from') + ' ' + `${data.from}`
        + ' '
        + this.translate.instant('proTasks.map.till') + ' ' + `${data.till}`
        + '</div>';
    }
  }

  public static createInfoLine(info: InfoWindowContent, name: string, value: any, units: string) {
    info.content += '<div class="info-line"><span class="info-label">'
      + name + '</span>: ' + value + ' ' + units + '</div>';
    info.bubbleHeight += 13;
  }

  private generateMarkedRoadContent(info: InfoWindowContent, data: IntersectingMarkedRoadEntry) {
    info.content += '<div class="popup-content"><div class="ol-popup-header">'
      + `<span class="map-popup__title">${this.translate.instant('proTasks.map.marked-road')}</span></div>`;
    info.content += '<div class="ol-info-data">';
    info.content += `<div style="text-align: center" class="info-line">${data.roadGroupTitle}</div>`;
    info.content += `<div style="text-align: center" class="info-line">${data.description}</div>`;
  }

  public createAlternativesInfoWindow(
    decodedPoly: google.maps.LatLng[],
    alternative: any,
    idx: number,
    polyline: any,
    map?: google.maps.Map) {

    let coords = this.resolveInfoPosition(decodedPoly, polyline);
    let content = '';
    if (alternative.tollsFree) {
      content += '<div class="info-line"><span class="info-label" style="color: #b94141">'
        + this.translate.instant('tasks.map.popup.avoids')
        + '</span></div>';
    }
    content += '<div style="display: flex; flex-direction: row">'
      + '<div style="margin-right: 10px"><div class="info-line"><span class="info-label">'
      + this.translate.instant('tasks.map.popup.distance')
      + '</span>: ' + this.distancePipe.transform(alternative.distance) + ' '
      + '</div>';
    content += '<div class="info-line"><span class="info-label">'
      + this.translate.instant('tasks.map.popup.duration')
      + '</span>: ' + this.durationPipe.transform(alternative.duration) + ' '
      + '</div>';
    if (alternative.tollsEntry != null) {
      content += '<div class="info-line"><span class="info-label">'
        + this.translate.instant('tasks.map.popup.tolls')
        + '</span>: ' + this.pricePipe.transform(alternative.tollsEntry.totalCost) + '</div></div>'
        + '<div style="display: flex; flex-direction: column; align-self: center">';
    }
    content += '</div></div>';
    let alternativeInfo = new google.maps.InfoWindow();
    alternativeInfo.setContent(content);
    alternativeInfo.setPosition(coords);
    alternativeInfo.open(map);
    return alternativeInfo;
  }

  private resolveInfoPosition(decodedPoly: google.maps.LatLng[], polyline: any) {
    let start = decodedPoly[Math.round(decodedPoly.length / 4)];
    let middle = decodedPoly[Math.round(decodedPoly.length / 2)];
    let end = decodedPoly[Math.round(decodedPoly.length / 4 * 3)];
    let polygon: any = polyline;
    let coords;
    if (!google.maps.geometry.poly.containsLocation(start, polyline)) {
      coords = start;
    } else if (!google.maps.geometry.poly.containsLocation(end, polyline)) {
      coords = end;
    } else {
      coords = middle;
    }
    return coords;
  }

  private static addInfoLine(info: InfoWindowContent, name: string, value: any, units: string) {
    info.content += '<div class="info-line"><span class="info-label">'
      + name + '</span> ' + value + ' ' + units + '</div>';
    info.bubbleHeight += 13;
  }

  public static getIconWithLink(
    action: string,
    icon: string,
    tooltip: string,
    translate: TranslateService,
    activeNgZone: string,
    valueNearIcon: string = '',
    tooltipValue: string = ''): string {

    return '<a class="icon-action" ' +
      'title ="' + translate.instant('monitor.map.title.' + tooltip) + tooltipValue + '"' +
      activeNgZone + action + '})">' + valueNearIcon + '<i class="' + icon + '"></i></a>';
  }

  public static addStationInfo(
    info: InfoWindowContent,
    poi: any,
    translate: TranslateService) {

    if (poi.globalTankPOIPrices != null) {
      MonitorMarkerService.showGlobalTankPrices(poi.globalTankPOIPrices, info);
    }
    if (MonitorMarkerService.isAS24Station(poi.poiGroup.id)) {
      MonitorMarkerService.showGlobalTankPrices({dieselPrice: poi.fuelPrice, adBluePrice: poi.adbluePrice}, info);
    }
    ProTasksMapMarkerService.addPoiGroupInfo(poi, info, translate);
  }

  private static addPoiGroupInfo(poi: MapPOIEntry, info: InfoWindowContent, translate: TranslateService) {
    if (poi.network) {
      ProTasksMapMarkerService.addInfoLine(info, translate.instant('monitor.poi.network'), ': ' + poi.network, '');
    }
    info.bubbleHeight += 13;
    if (poi.poiGroup != null && poi.poiGroup.name !== null && poi.poiGroup.name !== 'CRT_FUEL_STATIONS') {
      ProTasksMapMarkerService.addInfoLine(info, translate.instant('monitor.poi.group'), ': ' + poi.poiGroup.name, '');
    }
    if (poi.quantity) {
      ProTasksMapMarkerService.addInfoLine(info, translate.instant('monitor.poi.quantity'), ': ' + poi.quantity, '');
    }
    if (poi.comment) {
      ProTasksMapMarkerService.addInfoLine(info, translate.instant('monitor.poi.comment'), ': ' + poi.comment, '');
    }
    if (poi.link) {
      info.content += '<div class="info-line"><span class="info-label">'
        + translate.instant('monitor.poi.link')
        + '</span>: <a target="_blank" href="'
        + poi.link + '">' + poi.link + '</a></div>';
    }
  }

  private static addTaskInsertionButtons(
    info: InfoWindowContent,
    poi: any,
    translate: TranslateService,
    poiType: string,
    managerViewType?: string) {

    info.content += ProTasksMapMarkerService
      .taskInsertActionLink(poi, translate, 'autoImport', false, poiType, managerViewType, false);
    info.content += ProTasksMapMarkerService
      .taskInsertActionLink(poi, translate, 'importToLastPosition', true, poiType, managerViewType, false);
    if ('form' === managerViewType) {
      info.content += ProTasksMapMarkerService
        .taskInsertActionLink(poi, translate, 'importSelected', false, poiType, managerViewType, true);
    }
  }

  private static taskInsertActionLink(
    poi: any,
    translate: TranslateService,
    tooltip: string,
    autoInsert: boolean,
    poiType: string,
    managerViewType?: string,
    importSelected?: boolean) {

    const comment = poi.comment != null ? escapeForOnclickArg(poi.comment) : null;
    const name = poi.name != null ? escapeForOnclickArg(poi.name) : null;
    const contactlessType = TaskUtils.resolveContactlessType(poi);
    const parkingType = TaskUtils.resolveParkingType(poi);
    let refZoneRun = this.resolveActiveZone(managerViewType);

    return '<a class="icon-action"'
      + refZoneRun
      // eslint-disable-next-line max-len
      + `insertProTask(${poi.latitude}, ${poi.longitude}, ${autoInsert}, '${poi.externalId}', '${poi.id}', '${comment}', '${poiType}', '${contactlessType}', '${parkingType}', '${poi.emails}', '${name}', ${importSelected})})">`
      + `${translate.instant('proTasks.map.' + tooltip)}`
      + '</a>';
  }

  private canManageTasks(): boolean {
    return AuthStorage.isLoggedClientEmployee() || AuthStorage.getModules().includes(Module.MANAGEMENT);
  }

  private static resolveActiveZone(formZone: string): string {
    switch (formZone) {
      case 'form':
        return ProTasksMapMarkerService.FORM_NG_ZONE;
      case 'queue':
        return ProTasksMapMarkerService.QUEUE_NG_ZONE;
      case 'done':
        return ProTasksMapMarkerService.DONE_NG_ZONE;
      default:
        return ProTasksMapMarkerService.QUEUE_NG_ZONE;
    }
  }
}
